import { Button, Grid, InputAdornment, TextField, Typography } from '@mui/material';

import { useChangePassword } from 'packages/workspace/services/hooks/useChangePassword';
import useSession from 'packages/workspace/services/store/session-store';
import React, { Fragment, useActionState, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';
import ButtonWithIcon from './ButtonWithIcon';
import useAccount from 'packages/workspace/services/hooks/useAccount';
import PronounTextField from 'packages/workspace/design/widgets/form-elements/PronounTextField';
import * as forge from 'node-forge';

type ChangePasswordProps = {
  userId: string;
  isDrawerOpen: boolean;
  onClose: () => void;
};

export interface ChangePassword {
  previous_pwd: string;
  new_pwd: string;
  pronoun: string;
}

const defaultChangePassword: ChangePassword = {
  previous_pwd: '',
  new_pwd: '',
  pronoun: '',
};

export const encryptWithPublicKey = (key: string, data: string) => {
  const publicKey = forge.pki.publicKeyFromPem(key);

  const encrypted = publicKey.encrypt(data, 'RSA-OAEP', {
    md: forge.md.sha256.create(),
    mgf1: {
      md: forge.md.sha256.create(),
    },
  });

  return forge.util.encode64(encrypted);
};

const ChangePassword: React.FC<ChangePasswordProps> = (props) => {
  const session = useSession();
  const [showPwd, setShowPwd] = useState(false);
  const handleClickShowPwd = () => setShowPwd(!showPwd);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const handleClickShowNewPwd = () => setShowNewPwd(!showNewPwd);
  const { mutateAsync: mutateChangePassword } = useChangePassword();
  const { data: accountData, isSuccess } = useAccount(session.iamPrincipal ? session.iamPrincipal?.id : '');
  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    reset,
  } = useForm<ChangePassword>({ mode: 'onChange' });

  useEffect(() => {
    if (!props.isDrawerOpen) {
      reset(defaultChangePassword);
    }
  }, [props.isDrawerOpen, reset]);
  useEffect(() => {
    if (isSuccess && accountData) {
      setValue('pronoun', accountData?.data?.components?.pronoun);
    }
  }, [accountData, isSuccess]);

  const handleRoleSubmit = async (formData: ChangePassword) => {
    try {
      const response = await fetch('/api/core/changepwd');
      const { publicKey } = await response.json();
      const pemHeader = '-----BEGIN PUBLIC KEY-----\n';
      const pemFooter = '\n-----END PUBLIC KEY-----';
      let formattedKey = publicKey.replace(/(.{64})/g, '$1\n');
      const binaryData = pemHeader + formattedKey + pemFooter;
      let encOldPwd = encryptWithPublicKey(binaryData, formData.previous_pwd);
      let encNewPwd = encryptWithPublicKey(binaryData, formData.new_pwd);

      await mutateChangePassword(
        { old_password: encOldPwd, new_password: encNewPwd, pronoun: formData.pronoun },
        {
          onSuccess: () => {
            props.onClose();
          },
        }
      );
    } catch (error) {
      console.error('Error during encryption or password change:', error);
    }
  };
  const toBoolean = (str: string | undefined): boolean => str === 'true';
  const [_, formAction] = useActionState(handleSubmit(handleRoleSubmit), {});
  return (
    <Fragment>
      <div className="rhs-drawer-content">
        <div className="drawer-inner-container">
          <div className="drawer-header">
            <Typography variant="subtitle2" className="title">
              Change Password
            </Typography>
            <ButtonWithIcon onClick={props.onClose} />
          </div>
          <div className="drawer-body px-5">
            <form autoComplete="off" action={formAction} className="drawer-form-container">
              <Grid container spacing={3}>
                <Grid item sm={12}>
                  <Controller
                    name="previous_pwd"
                    render={({ field }: { field: unknown }) => (
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        error={Boolean(errors.previous_pwd)}
                        className="rhs-input py-2"
                        {...field}
                        id="previous_pwd"
                        type={showPwd ? 'text' : 'password'}
                        label="Previous Password"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <ButtonWithIcon Icon={showPwd ? MdVisibility : MdVisibilityOff} tooltip="Toggle password visibility" onClick={handleClickShowPwd} onMouseDown={handleClickShowPwd} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                    control={control}
                    defaultValue=""
                    rules={
                      {
                        // required: true,
                      }
                    }
                  />
                </Grid>
                <Grid item sm={12}>
                  <Controller
                    name="new_pwd"
                    render={({ field }: { field: unknown }) => (
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        error={Boolean(errors.new_pwd)}
                        {...field}
                        id="new_pwd"
                        type={showNewPwd ? 'text' : 'password'}
                        className="rhs-input py-2"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <ButtonWithIcon
                                Icon={showNewPwd ? MdVisibility : MdVisibilityOff}
                                tooltip="Toggle new password visibility"
                                onClick={handleClickShowNewPwd}
                                onMouseDown={handleClickShowNewPwd}
                              />
                            </InputAdornment>
                          ),
                        }}
                        label="New Password"
                      />
                    )}
                    control={control}
                    defaultValue=""
                    rules={{
                      // required: true,
                      minLength: parseInt(process.env.NEXT_PUBLIC_PASS_POLICY_MIN_LENGTH as string),
                      maxLength: parseInt(process.env.NEXT_PUBLIC_PASS_POLICY_MAX_LENGTH as string),
                      validate: {
                        lowerCase: (value) => {
                          if (toBoolean(process.env.NEXT_PUBLIC_PASS_POLICY_SPECIAL_LOWER_CASE_REQUIRED) === false) {
                            return true;
                          }
                          if (value) {
                            return /.*[a-z]+.*/.test(value);
                          }
                          return;
                        },
                        upperCase: (value) => {
                          if (toBoolean(process.env.NEXT_PUBLIC_PASS_POLICY_SPECIAL_UPPER_CASE_REQUIRED) === false) {
                            return true;
                          }
                          if (value) {
                            return /.*[A-Z]+.*/.test(value);
                          }
                          return;
                        },
                        specialChar: (value) => {
                          if (toBoolean(process.env.NEXT_PUBLIC_PASS_POLICY_SPECIAL_CHARACTER_REQUIRED) === false) {
                            return true;
                          }
                          if (value) {
                            return /[-!$%^&*()_+|~=`{}[\]:/;<>?,.@#]/.test(value);
                          }
                          return;
                        },
                        digit: (value) => {
                          if (toBoolean(process.env.NEXT_PUBLIC_PASS_POLICY_DIGITS_REQUIRED) === false) {
                            return true;
                          }
                          if (value) {
                            return /[0-9]/.test(value);
                          }
                          return;
                        },
                      },
                    }}
                  />
                </Grid>
                {errors.new_pwd?.type == 'required' && <span className="font-medium tracking-wide text-red-500 text-xs mt-1  validation-set">Password is required</span>}
                {errors.new_pwd?.type == 'upperCase' && <span className="font-medium tracking-wide text-red-500 text-xs mt-1  validation-set">Upper Case required</span>}
                {errors.new_pwd?.type == 'lowerCase' && <span className="font-medium tracking-wide text-red-500 text-xs mt-1  validation-set">Lower Case required</span>}
                {errors.new_pwd?.type == 'specialChar' && <span className="font-medium tracking-wide text-red-500 text-xs mt-1  validation-set">Special Char required</span>}
                {errors.new_pwd?.type == 'digit' && (
                  <span className="font-medium tracking-wide text-red-500 text-xs mt-1 validation-set">Minimum digit required: {process.env.NEXT_PUBLIC_PASS_POLICY_MIN_DIGITS}</span>
                )}
                {errors.new_pwd?.type == 'minLength' && (
                  <span className="font-medium tracking-wide text-red-500 text-xs mt-1  validation-set">Minimum length required : {process.env.NEXT_PUBLIC_PASS_POLICY_MIN_LENGTH}</span>
                )}
                {errors.new_pwd?.type == 'maxLength' && (
                  <span className="font-medium tracking-wide text-red-500 text-xs mt-1 validation-set">Maximum length required : {process.env.NEXT_PUBLIC_PASS_POLICY_MAX_LENGTH}</span>
                )}
                <Grid item sm={12}>
                  <PronounTextField name="pronoun" control={control} id="pronoun" label="Pronoun" placeholder="Enter pronoun here" />
                </Grid>
              </Grid>
              <div className="p-4 border-t border-bg-6 w-full text-right mt-auto">
                <Button className="button-primary mr-2" type="submit">
                  Update
                </Button>
                <Button className="button-secondary" onClick={props.onClose}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ChangePassword;
